import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import client from "@/lib/apiClient";
import { useLang } from "@/i18n";
import { Lu } from "@/components/shared";

const SUGGEST = {
  ar: ["خفّض التكييف إلى 21 درجة", "أطفئ الأنوار وأغلق الستائر", "أريد تنظيف الغرفة", "فعّل وضع النوم"],
  en: ["Set the AC to 21°", "Turn off lights and close curtains", "I need housekeeping", "Activate sleep scene"],
};
const ACT = {
  ar: { control: "تم تنفيذ التحكم", service: "تم إرسال الطلب" },
  en: { control: "Room updated", service: "Request sent" },
};

export function GuestConcierge({ room, onRoom }) {
  const { lang } = useLang();
  const L = lang === "ar";
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const sid = `concierge-${room.id}`;

  useEffect(() => { if (open) client.get(`/guest/concierge/history?session_id=${sid}`).then((r) => setMsgs(r.data)).catch(() => {}); }, [open, sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: q }]);
    try {
      const r = await client.post("/guest/concierge", { session_id: sid, message: q, room_id: room.id });
      setMsgs((m) => [...m, { role: "assistant", content: r.data.reply, actions: r.data.actions }]);
      if (r.data.actions.length) { onRoom(r.data.room); toast.success(ACT[lang][r.data.actions[0].type]); }
    } catch { setMsgs((m) => [...m, { role: "assistant", content: L ? "عذراً، تعذر الاتصال." : "Sorry, connection failed." }]); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="concierge-open" onClick={() => setOpen(true)}
        className="w-full flex items-center gap-3 p-3.5 rounded-2xl md-gold-gradient text-white active:scale-95 transition-transform shadow-[0_8px_24px_rgba(0,71,255,0.3)]">
        <span className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center"><Lu name="sparkles" className="w-5 h-5" /></span>
        <span className="text-start flex-1">
          <span className="block text-sm font-bold">{L ? "كونسيرج مدارك الذكي" : "Madarek AI Concierge"}</span>
          <span className="block text-[11px] text-white/80">{L ? "اطلب أي شيء بكلماتك: التكييف، الإضاءة، الخدمات" : "Ask anything: AC, lights, services"}</span>
        </span>
        <Lu name={L ? "chevron-left" : "chevron-right"} className="w-4 h-4 text-white/70" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "tween", duration: 0.3 }}
            data-testid="concierge-sheet" className="absolute inset-x-0 top-0 z-30 bg-card text-foreground flex flex-col rounded-3xl shadow-2xl" style={{ height: "min(640px, 100%)" }}>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
              <span className="w-9 h-9 rounded-xl md-gold-gradient flex items-center justify-center"><Lu name="sparkles" className="w-5 h-5 text-white" /></span>
              <div className="flex-1"><p className="font-bold leading-tight">{L ? "كونسيرج مدارك" : "Madarek Concierge"}</p><p className="text-[11px] text-muted-foreground">{L ? `غرفة ${room.number} · متصل` : `Room ${room.number} · online`}</p></div>
              <button data-testid="concierge-close" onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-foreground/10"><Lu name="x" className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 md-scrollbar-hidden" data-testid="concierge-messages">
              {msgs.length === 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">{L ? "أهلاً بك! كيف أقدر أخدمك؟" : "Welcome! How can I help?"}</p>
                  {SUGGEST[lang].map((s) => (
                    <button key={s} data-testid="concierge-suggestion" onClick={() => send(s)}
                      className="block w-full text-start text-sm px-3 py-2.5 rounded-xl bg-foreground/5 hover:bg-primary/10 border border-border text-foreground/80">{s}</button>
                  ))}
                </div>
              )}
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div data-testid={`concierge-msg-${m.role}`} className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground rounded-ee-md" : "bg-foreground/5 border border-border rounded-es-md"}`}>
                    {m.content}
                    {m.actions?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {m.actions.map((a, j) => <span key={j} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"><Lu name="check" className="w-3 h-3" />{a.type === "control" ? Object.keys(a.patch).join(", ") : a.kind}</span>)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {busy && <div className="flex justify-start"><div className="px-3.5 py-2.5 rounded-2xl bg-foreground/5 border border-border inline-flex gap-1"><span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" /><span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce [animation-delay:120ms]" /><span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce [animation-delay:240ms]" /></div></div>}
              <div ref={endRef} />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-border flex gap-2">
              <input data-testid="concierge-input" value={input} onChange={(e) => setInput(e.target.value)} disabled={busy}
                placeholder={L ? "اكتب طلبك..." : "Type your request..."}
                className="flex-1 h-11 px-3.5 rounded-xl bg-foreground/5 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
              <button data-testid="concierge-send" type="submit" disabled={busy || !input.trim()} className="w-11 h-11 rounded-xl md-gold-gradient text-white flex items-center justify-center disabled:opacity-40">
                <Lu name="send-horizontal" className={`w-4 h-4 ${L ? "rotate-180" : ""}`} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
